import { getNormalizedCustomAttributes } from "../redux/models/checkout"
import { getBikeTypeCategory, BikeTypeCategory } from "./bikeUtils"
import { stringToBase64 } from "./text"

/** Shopify variant IDs (base64), aligned with BicycleProductPage extended warranty */
export const EXTENDED_WARRANTY_OFFERS = {
  electric: {
    productSlug: "electric-warranty",
    variantId:
      "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC81MzgzOTkxNzc3NzI2OA==",
    priceValue: 149.99,
  },
  electricTrikes: {
    productSlug: "tricycle-warranty",
    variantId:
      "Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC81MzgzOTkxNzgxMDAzNg==",
    priceValue: 249.99,
  },
} as const

export function getExtendedWarrantyOfferForCategory(
  category: BikeTypeCategory
):
  | (typeof EXTENDED_WARRANTY_OFFERS)["electric"]
  | (typeof EXTENDED_WARRANTY_OFFERS)["electricTrikes"]
  | null {
  if (category === "electric") return EXTENDED_WARRANTY_OFFERS.electric
  if (category === "electricTrikes")
    return EXTENDED_WARRANTY_OFFERS.electricTrikes
  return null
}

export function warrantyLineIsExtended(
  warrantyNode: {
    merchandise?: { id?: string | null }
    variant?: { id?: string | null }
  } | undefined
): boolean {
  const rawId = warrantyNode?.merchandise?.id || warrantyNode?.variant?.id
  if (!rawId) return false
  const id = stringToBase64(rawId)
  return (
    id === EXTENDED_WARRANTY_OFFERS.electric.variantId ||
    id === EXTENDED_WARRANTY_OFFERS.electricTrikes.variantId
  )
}

/** Count cart line items that are the extended warranty SKU (may be standalone, not grouped under the bike). */
export function countExtendedWarrantyLinesInCart(
  lineItems:
    | {
        node: {
          attributes?: { key: string; value: string }[]
          customAttributes?: { key: string; value: string }[]
          merchandise?: { id?: string | null }
          variant?: { id?: string | null }
        }
      }[]
    | undefined
): number {
  if (!lineItems?.length) return 0
  let n = 0
  for (const { node } of lineItems) {
    const attrs = getNormalizedCustomAttributes(
      node.attributes || node.customAttributes
    )
    if (attrs["type"] !== "warranty") continue
    if (warrantyLineIsExtended(node)) n++
  }
  return n
}

export function isOpenBoxReturnBike(
  bicycle: { formattedTitle?: string | null } | undefined
): boolean {
  return (
    bicycle?.formattedTitle?.toUpperCase().includes("OPEN BOX RETURN") ?? false
  )
}

export function bikeQualifiesForExtendedWarrantyPrompt(
  bicycle:
    | {
        type?: Array<string | null> | null
        style?: Array<string | null> | null
        formattedTitle?: string | null
      }
    | undefined
): boolean {
  if (isOpenBoxReturnBike(bicycle)) return false
  return getExtendedWarrantyOfferForCategory(getBikeTypeCategory(bicycle || {})) !== null
}

/** Rich HTML for extended warranty modals (product page + cart). */
export function getExtendedWarrantyContent(): string {
  return `
        <div class="extended-warranty">
          <h3>Extended Warranty Coverage – Year 2 Protection</h3>

          <p>
            The <strong>sixthreezero Extended Warranty</strong> adds
            <strong>one additional year of coverage</strong> to your electric bike or
            electric trike, extending total coverage to
            <strong>24 months from the original delivery date</strong>.
          </p>

          <h4>What\u2019s Covered</h4>
          <p>
            During the second year of ownership, the extended warranty covers
            <strong>both parts and labor</strong> for the following components in the
            event of a manufacturer-related failure:
          </p>

          <h5>Electrical Components</h5>
          <ul>
            <li>Battery</li>
            <li>Motor</li>
            <li>Controller</li>
            <li>Display</li>
            <li>Wiring and electrical connections</li>
          </ul>

          <h5>Non-Electrical Components</h5>
          <ul>
            <li>Frame and structural components</li>
            <li>Drivetrain components</li>
            <li>Brakes (excluding wear items)</li>
          </ul>

          <h4>Exclusions</h4>
          <ul>
            <li>Tires and inner tubes</li>
            <li>Rims and wheels</li>
            <li>Normal wear-and-tear items</li>
            <li>
              Damage caused by misuse, accidents, water intrusion, or unauthorized
              modifications
            </li>
          </ul>

          <h4>Battery Warranty Process</h4>
          <p>If a battery warranty claim is submitted:</p>
          <ul>
            <li>The battery must be returned to sixthreezero for diagnostic testing</li>
            <li>Return shipping is the responsibility of the warranty holder</li>
            <li>Upon approval, a replacement battery will be shipped at no cost</li>
          </ul>

          <h4>Labor Coverage</h4>
          <ul>
            <li>
              Labor may be performed by a sixthreezero approved service provider or
              coordinated directly through sixthreezero
            </li>
          </ul>

          <h4>Important Notes</h4>
          <ul>
            <li>The extended warranty is optional and must be purchased separately</li>
            <li>
              Coverage begins immediately after the standard 12-month warranty expires
            </li>
            <li>The warranty is non-transferable.</li>
          </ul>
        </div>
    `.trim()
}
